import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { STATUS_META } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ORDER = ["new", "following_up", "won", "booked", "lost"];

export default function StageSelect({ customerId, status, size = "sm", className, onChanged }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[status] || { label: status, dot: "#64748B", cls: "bg-muted text-muted-foreground border-border" };

  const change = async (next) => {
    if (next === status) return;
    setSaving(true);
    try {
      await client.put(`/customers/${customerId}`, { status: next });
      toast.success(`Moved to ${STATUS_META[next]?.label || next}`);
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      onChanged?.(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update stage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select value={status} onValueChange={change} disabled={saving}>
      <SelectTrigger
        data-testid={`stage-select-${customerId}`}
        aria-label="Change stage"
        className={cn(
          "h-auto w-auto gap-1.5 rounded-full border px-2.5 font-semibold shadow-none focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-60",
          size === "sm" ? "py-0.5 text-[11px]" : "py-1 text-xs",
          meta.cls,
          className
        )}
      >
        {saving ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.dot }} />
        )}
        <span>{meta.label}</span>
      </SelectTrigger>
      <SelectContent>
        {ORDER.map((s) => (
          <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_META[s]?.dot }} />
              {STATUS_META[s]?.label || s}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
